import { Box, Typography, useTheme } from '@mui/material'
import { ZoomIn } from '@mui/icons-material'
import { ECGBackground } from './ECGBackground'
import { sampleECGData } from '../utils/sampleECGData'
import { ECGChart } from './ECGChart'

interface SingleECGLeadGraphProps {
  leadName: string
  data: number[]
  height?: number
  highlight?: boolean
  onClick?: () => void
  isRhythmStrip?: boolean
}

/**
 * Componente que renderiza um único gráfico de derivação de ECG
 * Exibe a onda, label da derivação e ícone de zoom quando interativo
 */
export const SingleECGLeadGraph = ({
  leadName,
  data,
  height = 120,
  highlight,
  onClick,
  isRhythmStrip = false
}: SingleECGLeadGraphProps) => {
  const theme = useTheme()
  // Lead de ritmo usa 10s, demais leads usam 2.5s (padrão)
  const durationSeconds = isRhythmStrip ? 10 : 2.5
  const chartData = sampleECGData(data, durationSeconds)

  // ECG paper standard: 25mm/s speed
  // time domain should be [0, durationSeconds * 25] in mm
  const maxTimeInMm = durationSeconds * 25

  return (
    <Box
      data-lead={leadName}
      sx={{
        width: '100%',
        height: height,
        position: 'relative',
        overflowX: { xs: 'auto', lg: 'hidden' },
        overflowY: 'hidden',
        border: `2px solid ${highlight ? theme.palette.ecgAlert.main : theme.palette.ecgNormal.main}`,
        cursor: onClick ? 'pointer !important' : 'default',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease, z-index 0s',
        '&::-webkit-scrollbar': {
          height: '6px'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#aaa',
          borderRadius: '3px'
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f5f5f5'
        },
        scrollbarHeight: 'thin',
        '&:hover': onClick
          ? {
              '*': {
                cursor: 'pointer !important'
              },
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
            }
          : {}
      }}
      onClick={onClick}
    >
      <ECGBackground maxTimeInMm={maxTimeInMm} />

      {/* Label do Lead */}
      <Typography
        variant='body2'
        sx={{
          position: 'absolute',
          top: 4,
          left: 8,
          fontWeight: 'bold',
          fontSize: '0.75rem',
          zIndex: 2,
          color: theme.palette.primary.main
        }}
      >
        {leadName}
      </Typography>

      {/* Ícone de Zoom (aparece no hover se clicável) */}
      {onClick && (
        <Box
          sx={{
            position: 'absolute',
            top: 4,
            right: 8,
            zIndex: 2,
            opacity: 0,
            transition: 'opacity 0.2s',
            '.MuiBox-root:hover &': {
              opacity: 1
            }
          }}
        >
          <ZoomIn
            sx={{ fontSize: '1rem', color: theme.palette.primary.main }}
            titleAccess='Zoom'
          />
        </Box>
      )}

      {/* Gráfico */}
      <ECGChart
        data={chartData}
        maxTimeInMm={maxTimeInMm}
        strokeWidth={0.5}
        heightInLargeSquares={4}
      />
    </Box>
  )
}
